'use client';

import { useEffect, useState } from 'react';
import { CateringWidget, type CateringWidgetTheme, type LocationOption } from './catering-widget';
import type { GroupMemberOption } from './group-switcher';
import type { SupportedLang } from '@/lib/widget-i18n';

interface FloatingWidgetProps {
  agentId: string;
  venueId: string;
  venueName: string;
  trackingId: string;
  menuUrl?: string;
  theme?: Partial<CateringWidgetTheme>;
  /** Circular/pill trigger image (logo). Shown when collapsed. */
  triggerLogoUrl: string | null;
  /** Background color of the trigger. Supports any CSS <color> or gradient. */
  triggerBackground: string;
  /** Optional label next to the logo. When set, trigger renders as a pill;
      when omitted, trigger renders as a 64px circle. */
  triggerLabel?: string;
  /** Optional short-label color override (defaults to triggerLogoTextColor). */
  triggerTextColor?: string;
  /** Accessible label for the trigger button. */
  triggerAriaLabel: string;
  /** Heading shown inside the expanded widget idle state. */
  titleOverride?: string;
  descriptionOverride?: string;
  /** Per-venue override for the idle "Chat" button label. Forwarded to
      CateringWidget. Undefined → widget uses the localized default. */
  chatLabelOverride?: string;
  logoHasOwnBorder?: boolean;
  /** Forwarded to CateringWidget — exposes a FR/EN toggle in the idle panel. */
  supportedLanguages?: SupportedLang[];
  /** Override the ambience audio URL. Forwarded to CateringWidget. The custom-
      element bundle passes an absolute URL so the file resolves on third-party
      hosts (host origin doesn't have BookOne's audio assets). */
  ambienceAudioUrl?: string;
  /** Origin used to build absolute URLs inside the widget (booking tracking
      link). Forwarded to CateringWidget. Bundle passes BookOne origin so the
      open_tracking_link call site doesn't resolve against the host's domain. */
  appOrigin?: string;
  /** Multi-location venue: render location-pick screen before idle. */
  requireLocationPick?: boolean;
  /** Locations available for selection. Required when requireLocationPick is true. */
  locations?: LocationOption[];
  /** Per-location event guide URLs (Notion). Wired to open_event_guide tool. */
  eventGuideUrls?: Record<string, string>;
  /** Hospitality-group siblings — forwarded to CateringWidget, which renders
      them as "other locations?" links to each sibling's own booking page.
      Empty/undefined for standalone venues. */
  groupMembers?: GroupMemberOption[];
  /** Anchor target for the group links — forwarded to CateringWidget. */
  groupLinkTarget?: '_self' | '_blank';
}

// Floating bottom-right corner widget. Collapsed state is a logo trigger
// (circle if no label, pill if labelled); click expands into a floating
// card containing the full CateringWidget. The card's own header hosts
// the minimize control — we pass onMinimize down so there's exactly one
// close affordance per state and nothing overlays the mic/mode/end row.
//
// State persists between open/close so a caller can minimize mid-chat
// without losing their intake. Unmounting would end the session.
export function FloatingWidget(props: FloatingWidgetProps) {
  const [open, setOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const hasLabel = Boolean(props.triggerLabel);

  // Embedded mode = running inside an iframe (e.g. via /widget-corner.js on a
  // foreign domain). When embedded, we postMessage state changes up to the
  // host so it can resize the iframe to fit the pill or expanded card. We
  // also strip the 24px viewport padding that the standalone demo page uses,
  // since the iframe IS the viewport — the host iframe element provides the
  // bottom-right offset itself.
  const isEmbedded = typeof window !== 'undefined' && window.parent !== window;

  // Mobile (≤ 640px): widget goes fullscreen with safe-area insets so the
  // iPhone notch / home indicator don't crowd the header + input row.
  // Desktop: floating bottom-right corner card. Re-measured on resize so
  // orientation change / split view reflow correctly.
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 640);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  // iOS keyboard handling for the corner pill expanded on mobile.
  // Mirrors what /book/<slug> does (book-client.tsx) — same canonical pattern
  // Slack web / Discord PWA / iMessage Web use for fullscreen chat on iOS.
  // Two effects layered together:
  //
  //   (a) visualViewport tracking — writes BOTH the visible viewport height
  //       AND the visual-viewport top-offset within the layout viewport into
  //       CSS variables. The panel's `top` follows --fw-viewport-offset so
  //       when iOS shifts the visual viewport down to bring the focused
  //       input into view, the panel travels with it instead of getting
  //       stranded above the visible area. The panel's `height` follows
  //       --fw-viewport-h so the chat input at its bottom always sits just
  //       above the keyboard.
  //
  //       Listening to BOTH `resize` AND `scroll` events on visualViewport:
  //       offsetTop changes fire `scroll`, not `resize`. Throttled via
  //       requestAnimationFrame so iOS's scroll-event storm during keyboard
  //       animation doesn't trigger style-recalc storms.
  //
  //   (b) body scroll lock on the host page (or iframe document), with
  //       prev-style restore on cleanup. Without this, the venue's own
  //       page scrolls underneath the panel during typing and iOS shows
  //       its URL chip mid-content.
  //
  // Both gated on `open && isMobile`. Desktop corner card and collapsed
  // pill don't trigger either — leaves the venue's site untouched when
  // the chat isn't actively in use.
  useEffect(() => {
    if (!open || !isMobile) return;
    const vv = window.visualViewport;
    let rafId = 0;
    const update = vv
      ? () => {
          rafId = 0;
          const root = document.documentElement;
          root.style.setProperty('--fw-viewport-h', `${vv.height}px`);
          root.style.setProperty('--fw-viewport-offset', `${vv.offsetTop}px`);
        }
      : null;
    const onChange = vv
      ? () => {
          if (rafId) return;
          rafId = requestAnimationFrame(update!);
        }
      : null;
    if (vv && update && onChange) {
      update();
      vv.addEventListener('resize', onChange);
      vv.addEventListener('scroll', onChange);
    }
    // Body lock — overflow:hidden ONLY, no position:fixed.
    //
    // Critical: do NOT set `position: fixed` on the host body. On iOS
    // Safari, when a focused input lives inside a fixed-positioned
    // overlay, iOS's keyboard auto-scroll tries to bring the input into
    // view by shifting the layout viewport. If body is `position: fixed`
    // it can't follow that shift, and the user's first tap on the chat
    // input flicks the panel off-screen — they have to manually scroll
    // back to it. Same root cause and same fix as commit fd20f35 on
    // /book/<slug>: lock the panel (already done above via top:offset),
    // not the body.
    //
    // overflow:hidden alone is enough to prevent the venue's own page
    // from scrolling underneath the panel and to keep iOS's URL chip
    // pinned (no chip-mid-content overlay during typing).
    //
    // Defensive save+restore on cleanup since we're mutating the
    // venue's body on third-party sites.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      if (vv && onChange) {
        vv.removeEventListener('resize', onChange);
        vv.removeEventListener('scroll', onChange);
      }
      const root = document.documentElement;
      root.style.removeProperty('--fw-viewport-h');
      root.style.removeProperty('--fw-viewport-offset');
      document.body.style.overflow = prevOverflow;
    };
  }, [open, isMobile]);

  // Emit expand/collapse postMessage to the host page when embedded.
  // widget-corner.js listens on the host side and resizes the iframe to
  // 432×728 (desktop) or 100vw×100dvh (mobile) on expand, and back to
  // 340×108 on collapse. Origin is wildcarded here because the loader
  // script can be installed on any host — the parent validates origin.
  useEffect(() => {
    if (!isEmbedded) return;
    try {
      window.parent.postMessage({ type: open ? 'bookone:expand' : 'bookone:collapse' }, '*');
    } catch {
      // postMessage can throw in unusual cross-origin sandbox configurations.
      // Non-fatal — the iframe just stays at its current size.
    }
  }, [open, isEmbedded]);

  // Respect prefers-reduced-motion. Disable the pill→card transition for
  // users who've opted out of motion in their OS settings.
  const reducedMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const triggerTransition = reducedMotion ? 'none' : 'opacity 180ms ease, transform 180ms ease';
  const panelTransition = reducedMotion ? 'none' : 'opacity 220ms ease, transform 220ms ease';

  return (
    <>
      <button
        type="button"
        aria-label={props.triggerAriaLabel}
        onClick={() => setOpen(true)}
        style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          display: 'inline-flex',
          alignItems: 'center',
          gap: hasLabel ? 12 : 0,
          padding: hasLabel ? '10px 20px 10px 10px' : 0,
          minWidth: hasLabel ? undefined : 64,
          width: hasLabel ? 'auto' : 64,
          height: 64,
          borderRadius: hasLabel ? 999 : '50%',
          background: props.triggerBackground,
          color: props.triggerTextColor ?? '#F5F0E6',
          border: 'none',
          cursor: 'pointer',
          boxShadow: '0 12px 32px rgba(0, 0, 0, 0.35)',
          overflow: 'hidden',
          opacity: open ? 0 : 1,
          transform: open ? 'scale(0.85)' : 'scale(1)',
          pointerEvents: open ? 'none' : 'auto',
          transition: triggerTransition,
          zIndex: 9998,
          fontFamily: 'inherit',
        }}
      >
        {props.triggerLogoUrl && (
          <span
            style={{
              width: 44,
              height: 44,
              borderRadius: '50%',
              overflow: 'hidden',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              // Subtle white halo lifts standard-variant BookOne brand logos
              // off dark pills. For venues whose logo already has its own
              // styling baked in (Akiro bowl, Members Only wordmark, Boxcar
              // wordmark, Ottavio wordmark — all pass logoHasOwnBorder=true)
              // the halo reads as a visible gray spot. Skip it for those.
              background: hasLabel && !props.logoHasOwnBorder ? 'rgba(255, 255, 255, 0.08)' : 'transparent',
            }}
          >
            <img
              src={props.triggerLogoUrl}
              alt=""
              style={{
                width: hasLabel ? '85%' : '100%',
                height: hasLabel ? '85%' : '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          </span>
        )}
        {hasLabel && (
          <span
            style={{
              fontSize: 14,
              fontWeight: 500,
              letterSpacing: '0.01em',
              whiteSpace: 'nowrap',
            }}
          >
            {props.triggerLabel}
          </span>
        )}
      </button>

      {/* Expanded panel — fullscreen sheet on mobile (with iPhone safe-area
          insets so content doesn't hide under the notch or home indicator),
          floating bottom-right card on desktop. */}
      <div
        role="dialog"
        aria-label="Event booking assistant"
        aria-hidden={!open}
        style={{
          position: 'fixed',
          ...(isMobile
            ? {
                // Pin the panel to the VISUAL VIEWPORT (not the layout
                // viewport). When iOS keyboard opens it shifts the visual
                // viewport down to bring the focused input into view; we
                // follow via --fw-viewport-offset (window.visualViewport
                // .offsetTop, set by the effect above). Combined with the
                // viewport-tracked height var, the panel stays exactly
                // aligned with what the user sees, with the chat input at
                // its bottom always just above the keyboard. Falls back to
                // top:0 / 100dvh if the API isn't available.
                //
                // No `bottom: 0` — when `top` follows offsetTop, anchoring
                // bottom to layout-viewport bottom would stretch the panel
                // past its --fw-viewport-h. Top + height drive geometry,
                // matching the .hosted-widget-shell CSS contract on
                // /book/<slug>.
                top: 'var(--fw-viewport-offset, 0)',
                left: 0,
                right: 0,
                width: '100vw',
                maxHeight: 'var(--fw-viewport-h, 100dvh)',
                height: 'var(--fw-viewport-h, 100dvh)',
                borderRadius: 0,
                paddingTop: 'env(safe-area-inset-top)',
                paddingBottom: 'env(safe-area-inset-bottom)',
                paddingLeft: 'env(safe-area-inset-left)',
                paddingRight: 'env(safe-area-inset-right)',
              }
            : isEmbedded
              ? {
                  // Embedded mode: iframe is the viewport. Fill it edge-to-edge,
                  // keep rounded corners (the iframe is transparent — only the
                  // panel's borderRadius reads on the host page).
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  width: '100%',
                  maxHeight: '100%',
                  borderRadius: 20,
                }
              : {
                  bottom: 24,
                  right: 24,
                  width: 'min(400px, calc(100vw - 32px))',
                  maxHeight: 'min(680px, calc(100dvh - 48px))',
                  borderRadius: 20,
                }),
          display: 'flex',
          flexDirection: 'column',
          background: props.theme?.backgroundColor ?? 'rgba(51, 21, 21, 0.95)',
          backdropFilter: 'blur(18px) saturate(1.3)',
          WebkitBackdropFilter: 'blur(18px) saturate(1.3)',
          border: isMobile ? 'none' : `1px solid ${props.theme?.borderColor ?? 'rgba(247, 179, 23, 0.18)'}`,
          overflow: 'hidden',
          boxShadow: isMobile ? 'none' : '0 24px 64px rgba(0, 0, 0, 0.45)',
          opacity: open ? 1 : 0,
          transform: open ? 'translateY(0)' : 'translateY(12px)',
          pointerEvents: open ? 'auto' : 'none',
          transition: panelTransition,
          zIndex: 9999,
        }}
      >
        <div style={{ flex: 1, overflow: 'auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
          <CateringWidget
            agentId={props.agentId}
            venueId={props.venueId}
            venueName={props.venueName}
            trackingId={props.trackingId}
            menuUrl={props.menuUrl}
            titleOverride={props.titleOverride}
            descriptionOverride={props.descriptionOverride}
            chatLabelOverride={props.chatLabelOverride}
            logoHasOwnBorder={props.logoHasOwnBorder}
            supportedLanguages={props.supportedLanguages}
            ambienceAudioUrl={props.ambienceAudioUrl}
            appOrigin={props.appOrigin}
            theme={props.theme}
            requireLocationPick={props.requireLocationPick}
            locations={props.locations}
            eventGuideUrls={props.eventGuideUrls}
            groupMembers={props.groupMembers}
            groupLinkTarget={props.groupLinkTarget}
            // Flex-fill the chat surface only when the corner pill is
            // expanded fullscreen on mobile. Desktop corner card keeps
            // today's behavior (transcript fixed at 240-320px) — its
            // 680px max parent doesn't need flex-fill, and changing it
            // would alter the desktop chat layout that's already tuned.
            fillContainer={isMobile && open}
            onMinimize={() => setOpen(false)}
          />
        </div>
      </div>
    </>
  );
}
